import React, { useState, useEffect } from 'react';
import {
  Box,
  Collapse,
  Typography,
  IconButton,
  Slider,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as FitToWidthIcon,
  CenterFocusStrong as FitToPageIcon,
  Refresh as ResetIcon,
  TextFields as FontSizeIcon
} from '@mui/icons-material';

const ZoomControls = ({ onZoomChange }) => {
  const [zoom, setZoom] = useState(100);
  const [fontSize, setFontSize] = useState(16);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [fitToWidth, setFitToWidth] = useState(false);
  const [readingMode, setReadingMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const zoomLevels = [50, 75, 100, 125, 150, 175, 200, 250, 300];
  const fontSizes = [12, 14, 16, 18, 20, 22, 24, 28, 32];

  useEffect(() => {
    // Notify parent component of zoom changes
    onZoomChange && onZoomChange({
      zoom,
      fontSize,
      lineHeight,
      fitToWidth,
      readingMode
    });
  }, [zoom, fontSize, lineHeight, fitToWidth, readingMode, onZoomChange]);

  const handleSliderChange = (event, newValue) => {
    setZoom(newValue);
  };

  const zoomIn = () => {
    const currentIndex = zoomLevels.findIndex(level => level >= zoom);
    const safeIndex = currentIndex === -1 ? zoomLevels.indexOf(100) : currentIndex;
    const nextIndex = Math.min(safeIndex + 1, zoomLevels.length - 1);
    setZoom(zoomLevels[nextIndex]);
  };

  const zoomOut = () => {
    const currentIndex = zoomLevels.findIndex(level => level >= zoom);
    const safeIndex = currentIndex === -1 ? zoomLevels.indexOf(100) : currentIndex;
    const prevIndex = Math.max(safeIndex - 1, 0);
    setZoom(zoomLevels[prevIndex]);
  };

  const resetZoom = () => {
    setZoom(100);
    setFontSize(16);
    setLineHeight(1.5);
    setFitToWidth(false);
    setReadingMode(false);
  };

  const fitToWidthToggle = () => {
    setFitToWidth(!fitToWidth);
    if (!fitToWidth) {
      setZoom(100); // Reset zoom when fitting to width
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Zoom Level Display */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Zoom: {zoom}%
        </Typography>
        
        {/* Quick Zoom Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={zoomOut} size="small">
            <ZoomOutIcon />
          </IconButton>
          
          <IconButton onClick={resetZoom} size="small">
            <ResetIcon />
          </IconButton>
          
          <IconButton onClick={zoomIn} size="small">
            <ZoomInIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Zoom Slider */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Zoom Level
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={zoom}
            onChange={handleSliderChange}
            min={25}
            max={500}
            step={25}
            marks={[
              { value: 50, label: '50%' },
              { value: 100, label: '100%' },
              { value: 200, label: '200%' },
              { value: 300, label: '300%' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
            sx={{
              '& .MuiSlider-markLabel': {
                fontSize: '0.75rem',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap'
              },
              '& .MuiSlider-mark': {
                height: 8,
                width: 2
              }
            }}
          />
        </Box>
      </Box>

      {/* Preset Zoom Levels */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
        {zoomLevels.map(level => (
          <Button
            key={level}
            size="small"
            variant={zoom === level ? "contained" : "outlined"}
            onClick={() => setZoom(level)}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {level}%
          </Button>
        ))}
      </Box>

      <Button
        size="small"
        variant="text"
        onClick={() => setShowAdvanced(prev => !prev)}
        sx={{ mb: 1, textTransform: 'none' }}
      >
        {showAdvanced ? 'Hide advanced' : 'Advanced'}
      </Button>

      <Collapse in={showAdvanced}>
        <Divider sx={{ mb: 2 }} />

        {/* Font Size Controls */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontSizeIcon fontSize="small" />
            Text Settings
          </Typography>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Font Size</InputLabel>
            <Select
              value={fontSize}
              onChange={(e) => setFontSize(e.target.value)}
              label="Font Size"
            >
              {fontSizes.map(size => (
                <MenuItem key={size} value={size}>
                  {size}px
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Line Height: {lineHeight}
          </Typography>
          <Slider
            value={lineHeight}
            onChange={(e, value) => setLineHeight(value)}
            min={1.0}
            max={3.0}
            step={0.1}
            marks={[
              { value: 1.0, label: 'Tight' },
              { value: 1.5, label: 'Normal' },
              { value: 2.0, label: 'Loose' },
              { value: 3.0, label: 'Very Loose' }
            ]}
            valueLabelDisplay="auto"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Display Options */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Display Options
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={fitToWidth}
                onChange={fitToWidthToggle}
              />
            }
            label="Fit to width"
            sx={{ display: 'block', mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={readingMode}
                onChange={(e) => setReadingMode(e.target.checked)}
              />
            }
            label="Reading mode"
            sx={{ display: 'block', mb: 1 }}
          />
        </Box>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={fitToWidthToggle}
            startIcon={<FitToWidthIcon />}
            sx={{ flex: 1 }}
          >
            Fit Width
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setZoom(100)}
            startIcon={<FitToPageIcon />}
            sx={{ flex: 1 }}
          >
            Actual Size
          </Button>
        </Box>

        {/* Reading Mode Info */}
        {readingMode && (
          <Box sx={{ p: 1, backgroundColor: 'primary.50', borderRadius: 1, mb: 2 }}>
            <Typography variant="caption" color="primary.main">
              Reading mode active: Simplified layout for better focus
            </Typography>
          </Box>
        )}
      </Collapse>

      {/* Keyboard Shortcuts Help */}
      <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Shortcuts:</strong> Ctrl + Plus/Minus to zoom, Ctrl+0 to reset
        </Typography>
      </Box>
    </Box>
  );
};

export default ZoomControls;
